// view-truncate-ok-file: ô ứng dụng dạng lưới cố định trên điện thoại
import { useState, useMemo } from "react";
import { useRouter } from "@tanstack/react-router";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DUAL_AXIS_NAVIGATION } from "@/config/navigation";
import { useAuthStore } from "@/stores/auth.store";
import { cn, getUploadUrl } from "@/lib/utils";
import {
    LayoutDashboard,
    ShoppingCart,
    FileText,
    Settings,
    Factory,
    Package,
    ClipboardCheck,
    Truck,
    Box,
    Users2,
    Layers,
    Scissors,
    Activity,
    Warehouse,
    ClipboardList,
    Cpu,
    Briefcase,
    HelpCircle,
    BookOpen,
    History,
    CheckCircle2,
    AlertTriangle,
    FileWarning,
    Microscope,
    Container,
    KanbanSquare,
    MonitorPlay,
    ArrowRightLeft,
    CreditCard,
    PieChart,
    BarChart,
    BarChart3,
    GitBranch,
    Printer,
    MessageSquarePlus,
    ScanBarcode,
    Trash2,
    Percent,
    PenTool,
    Copy,
    Wrench,
    FileSpreadsheet,
    PackageCheck,
    Calculator,
    ShieldCheck,
    Database,
    Users,
    ShieldAlert,
    Gauge,
    Building2,
    Search,
    X,
    Sparkles,
    type LucideIcon,
} from "lucide-react";

const IconMap: Record<string, LucideIcon> = {
    LayoutDashboard,
    ShoppingCart,
    FileText,
    Settings,
    Factory,
    Package,
    ClipboardCheck,
    Truck,
    Box,
    Users2,
    Layers,
    Scissors,
    Activity,
    Warehouse,
    ClipboardList,
    Cpu,
    Briefcase,
    HelpCircle,
    BookOpen,
    History,
    CheckCircle2,
    AlertTriangle,
    FileWarning,
    Microscope,
    Container,
    KanbanSquare,
    MonitorPlay,
    ArrowRightLeft,
    CreditCard,
    PieChart,
    BarChart,
    BarChart3,
    GitBranch,
    Printer,
    MessageSquarePlus,
    ScanBarcode,
    Trash2,
    Percent,
    PenTool,
    Copy,
    Wrench,
    FileSpreadsheet,
    PackageCheck,
    Calculator,
    ShieldCheck,
    Database,
    Users,
    ShieldAlert,
    Gauge,
};

function getModuleTheme(href: string) {
    if (href === "/" || href.includes("dashboard")) {
        return {
            bg: "bg-emerald-50 hover:bg-emerald-100/80 active:bg-emerald-100 border-emerald-200/60 shadow-emerald-500/10",
            iconColor: "text-emerald-600",
        };
    }
    if (href.startsWith("/quotations") || href.startsWith("/pricing")) {
        return {
            bg: "bg-amber-50 hover:bg-amber-100/80 active:bg-amber-100 border-amber-200/60 shadow-amber-500/10",
            iconColor: "text-amber-600",
        };
    }
    if (href.startsWith("/orders")) {
        return {
            bg: "bg-blue-50 hover:bg-blue-100/80 active:bg-blue-100 border-blue-200/60 shadow-blue-500/10",
            iconColor: "text-blue-600",
        };
    }
    if (href.startsWith("/customers") || href.startsWith("/vendors")) {
        return {
            bg: "bg-indigo-50 hover:bg-indigo-100/80 active:bg-indigo-100 border-indigo-200/60 shadow-indigo-500/10",
            iconColor: "text-indigo-600",
        };
    }
    if (href.startsWith("/products") || href.startsWith("/specs")) {
        return {
            bg: "bg-purple-50 hover:bg-purple-100/80 active:bg-purple-100 border-purple-200/60 shadow-purple-500/10",
            iconColor: "text-purple-600",
        };
    }
    if (href.startsWith("/delivery") || href.startsWith("/shipping")) {
        return {
            bg: "bg-teal-50 hover:bg-teal-100/80 active:bg-teal-100 border-teal-200/60 shadow-teal-500/10",
            iconColor: "text-teal-600",
        };
    }
    if (href.startsWith("/production-orders") || href.startsWith("/process-planning")) {
        return {
            bg: "bg-sky-50 hover:bg-sky-100/80 active:bg-sky-100 border-sky-200/60 shadow-sky-500/10",
            iconColor: "text-sky-600",
        };
    }
    if (href.startsWith("/factory") || href.startsWith("/mes")) {
        return {
            bg: "bg-rose-50 hover:bg-rose-100/80 active:bg-rose-100 border-rose-200/60 shadow-rose-500/10",
            iconColor: "text-rose-600",
        };
    }
    if (href.startsWith("/layouts") || href.startsWith("/designs") || href.startsWith("/prepress")) {
        return {
            bg: "bg-violet-50 hover:bg-violet-100/80 active:bg-violet-100 border-violet-200/60 shadow-violet-500/10",
            iconColor: "text-violet-600",
        };
    }
    if (href.startsWith("/molds") || href.startsWith("/plates") || href.startsWith("/tooling")) {
        return {
            bg: "bg-amber-50 hover:bg-amber-100/80 active:bg-amber-100 border-amber-300/70 shadow-amber-600/10",
            iconColor: "text-amber-700",
        };
    }
    if (href.startsWith("/qc") || href.startsWith("/quality") || href.startsWith("/ncr")) {
        return {
            bg: "bg-red-50 hover:bg-red-100/80 active:bg-red-100 border-red-200/60 shadow-red-500/10",
            iconColor: "text-red-600",
        };
    }
    if (href.startsWith("/warehouse") || href.startsWith("/materials") || href.startsWith("/inventory")) {
        return {
            bg: "bg-emerald-50 hover:bg-emerald-100/80 active:bg-emerald-100 border-emerald-200/60 shadow-emerald-500/10",
            iconColor: "text-emerald-700",
        };
    }
    if (href.startsWith("/purchasing") || href.startsWith("/outsource")) {
        return {
            bg: "bg-orange-50 hover:bg-orange-100/80 active:bg-orange-100 border-orange-200/60 shadow-orange-500/10",
            iconColor: "text-orange-600",
        };
    }
    if (href.startsWith("/finance") || href.startsWith("/debts") || href.startsWith("/reports")) {
        return {
            bg: "bg-cyan-50 hover:bg-cyan-100/80 active:bg-cyan-100 border-cyan-200/60 shadow-cyan-500/10",
            iconColor: "text-cyan-700",
        };
    }
    // Master data & settings
    return {
        bg: "bg-slate-100 hover:bg-slate-200/80 active:bg-slate-200 border-slate-200/80 shadow-slate-500/10",
        iconColor: "text-slate-700",
    };
}

interface MobileAppHubSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function MobileAppHubSheet({ open, onOpenChange }: MobileAppHubSheetProps) {
    const router = useRouter();
    const currentUser = useAuthStore((s) => s.user);
    const [searchKeyword, setSearchKeyword] = useState("");

    const userName = currentUser?.fullName || "Người dùng";
    const userRole = currentUser?.role || "USER";
    const userAvatar = currentUser?.avatar || null;
    const initials = userName
        .split(" ")
        .map((w: string) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    // 1. Phân nhóm danh mục nghiệp vụ và lọc theo RBAC user.role
    const sections = useMemo(() => {
        const keyword = searchKeyword.trim().toLowerCase();

        // Lọc danh mục domains theo role
        const accessibleDomains = DUAL_AXIS_NAVIGATION.filter((domain) => {
            if (!domain.roles) return true;
            if (!currentUser) return false;
            return domain.roles.includes(currentUser.role);
        });

        // Gom nhóm theo Section kèm khử trùng lặp module trong cùng phân hệ
        const sectionMap = new Map<string, Array<{ domainTitle: string; tab: any }>>();
        const sectionSeenHrefs = new Map<string, Set<string>>();

        accessibleDomains.forEach((domain) => {
            const accessibleTabs = domain.tabs.filter((tab) => {
                if (!tab.roles) return true;
                if (!currentUser) return false;
                return tab.roles.includes(currentUser.role);
            });

            accessibleTabs.forEach((tab) => {
                // Kiểm tra từ khóa tìm kiếm
                if (keyword) {
                    const matchTitle = tab.title.toLowerCase().includes(keyword);
                    const matchDomain = domain.title.toLowerCase().includes(keyword);
                    const matchSection = domain.section.toLowerCase().includes(keyword);
                    const matchHref = tab.href.toLowerCase().includes(keyword);
                    if (!matchTitle && !matchDomain && !matchSection && !matchHref) {
                        return;
                    }
                }

                const seen = sectionSeenHrefs.get(domain.section) || new Set<string>();
                if (seen.has(tab.href)) return; // Triệt tiêu module lặp lại trong cùng phân hệ
                seen.add(tab.href);
                sectionSeenHrefs.set(domain.section, seen);

                const list = sectionMap.get(domain.section) || [];
                list.push({ domainTitle: domain.title, tab });
                sectionMap.set(domain.section, list);
            });
        });

        // Trả về mảng các section có ít nhất 1 item
        const result: Array<{ title: string; items: Array<{ domainTitle: string; tab: any }> }> = [];
        sectionMap.forEach((items, sectionTitle) => {
            if (items.length > 0) {
                result.push({ title: sectionTitle, items });
            }
        });

        return result;
    }, [currentUser, searchKeyword]);

    const handleNavigate = (href: string) => {
        onOpenChange(false);
        setTimeout(() => {
            router.navigate({ to: href as any });
        }, 80);
    };

    // Quick Access items (Top 4 chức năng cốt lõi)
    const quickAccessItems = [
        { title: "Dashboard", href: "/", icon: LayoutDashboard, theme: getModuleTheme("/") },
        { title: "Đơn hàng", href: "/orders", icon: ShoppingCart, theme: getModuleTheme("/orders") },
        { title: "Lệnh SX", href: "/production-orders", icon: ClipboardList, theme: getModuleTheme("/production-orders") },
        { title: "Giao hàng", href: "/delivery", icon: Truck, theme: getModuleTheme("/delivery") },
    ];

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                showCloseButton={false}
                data-testid="mobile-app-hub-sheet-content"
                className="h-[92vh] max-h-[92vh] rounded-t-3xl p-0 overflow-hidden flex flex-col bg-white/95 backdrop-blur-xl border-t border-slate-200/80 shadow-2xl z-[70]"
            >
                {/* 1. Drag Handle trên đỉnh */}
                <div className="pt-2 pb-1 flex justify-center shrink-0">
                    <div className="w-12 h-1.5 bg-slate-300 rounded-full" />
                </div>

                <SheetHeader className="sr-only">
                    <SheetTitle>Cổng Ứng Dụng APV Manager</SheetTitle>
                </SheetHeader>

                {/* 2. User Profile Card Header */}
                <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100 bg-slate-50/70 shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="h-11 w-11 ring-2 ring-white shadow-sm shrink-0">
                            <AvatarImage src={getUploadUrl(userAvatar) || "/avatars/user.png"} alt={userName} />
                            <AvatarFallback className="apv-avatar-bg text-white font-bold text-xs">
                                {initials}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col min-w-0">
                            <div className="flex items-center gap-1.5">
                                <span className="text-sm font-bold text-slate-900 truncate">{userName}</span>
                                <Badge variant="secondary" className="text-[9px] h-4 px-1.5 font-bold apv-accent-badge uppercase shrink-0">
                                    {userRole}
                                </Badge>
                            </div>
                            <div className="flex items-center gap-1 text-[11px] text-slate-500 font-medium truncate mt-0.5">
                                <Building2 className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                                <span className="truncate">Nhà máy & Trụ sở Long An</span>
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        data-testid="mobile-app-hub-close-btn"
                        className="h-8 w-8 rounded-full bg-slate-200/70 hover:bg-slate-300/70 active:scale-95 flex items-center justify-center text-slate-600 transition-transform shrink-0"
                        aria-label="Đóng Cổng Ứng Dụng"
                    >
                        <X className="w-4.5 h-4.5" />
                    </button>
                </div>

                {/* 3. Thanh Tìm Kiếm Tức Thời */}
                <div className="px-4 pt-3 pb-2 shrink-0">
                    <div className="relative w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            type="search"
                            data-testid="mobile-app-hub-search-input"
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                            placeholder="Tìm nhanh chức năng (Đơn hàng, Báo giá, KCS...)..."
                            className="pl-9 pr-9 h-10 text-xs bg-slate-100/90 border-slate-200/80 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20"
                        />
                        {searchKeyword && (
                            <button
                                type="button"
                                onClick={() => setSearchKeyword("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                {/* 4. Vùng Nội Dung Cuộn: Quick Access + Phân Hệ Bento Grid */}
                <ScrollArea className="flex-1 px-4 py-2 overflow-y-auto">
                    {/* Cụm Quick Access (Top 4 Chức Năng Thường Dùng) - Ẩn khi đang gõ tìm kiếm */}
                    {!searchKeyword && (
                        <div className="mb-4 bg-slate-50/80 border border-slate-100 rounded-2xl p-2.5 shadow-xs">
                            <div className="flex items-center gap-1.5 px-1.5 pb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                                <span>Truy Cập Nhanh</span>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                                {quickAccessItems.map((item) => {
                                    const IconComponent = item.icon;
                                    return (
                                        <button
                                            key={`quick-${item.href}`}
                                            type="button"
                                            onClick={() => handleNavigate(item.href)}
                                            className="flex flex-col items-center group active:scale-95 transition-transform"
                                        >
                                            <div
                                                className={cn(
                                                    "w-13 h-13 rounded-2xl flex items-center justify-center border shadow-xs transition-colors",
                                                    item.theme.bg
                                                )}
                                            >
                                                <IconComponent className={cn("w-6 h-6", item.theme.iconColor)} />
                                            </div>
                                            <span className="text-[11px] font-medium text-slate-700 text-center leading-tight line-clamp-1 mt-1.5 px-0.5">
                                                {item.title}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Danh Sách Các Phân Hệ 4 Cột Bento Squircle */}
                    {sections.length > 0 ? (
                        <div className="space-y-4 pb-6">
                            {sections.map((sec) => (
                                <div key={sec.title} className="space-y-2">
                                    {/* Tiêu đề phân hệ */}
                                    <div className="flex items-center justify-between px-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-bold text-slate-800 tracking-tight">
                                                {sec.title}
                                            </span>
                                            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-200/70 text-slate-600 font-semibold">
                                                {sec.items.length}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Lưới Bento 4 Cột Squircle */}
                                    <div className="grid grid-cols-4 gap-y-3.5 gap-x-2">
                                        {sec.items.map(({ tab }) => {
                                            const IconComponent = (tab.icon && IconMap[tab.icon]) || Box;
                                            const theme = getModuleTheme(tab.href);

                                            return (
                                                <button
                                                    key={tab.href}
                                                    type="button"
                                                    data-testid={`app-hub-item-${tab.href.replace(/[^a-zA-Z0-9]/g, "-")}`}
                                                    onClick={() => handleNavigate(tab.href)}
                                                    className="flex flex-col items-center relative group active:scale-90 transition-transform duration-150 select-none"
                                                >
                                                    {/* Khối Squircle Đa Sắc Màu */}
                                                    <div
                                                        className={cn(
                                                            "w-14 h-14 rounded-2xl flex items-center justify-center border shadow-xs transition-shadow",
                                                            theme.bg
                                                        )}
                                                    >
                                                        <IconComponent className={cn("w-6 h-6", theme.iconColor)} />
                                                    </div>

                                                    {/* Badge nếu có */}
                                                    {tab.badge && (
                                                        <span className="absolute top-0 right-1 text-[8px] font-bold px-1 py-0.2 rounded-full bg-amber-500 text-white shadow-xs">
                                                            {tab.badge}
                                                        </span>
                                                    )}

                                                    {/* Nhãn chức năng */}
                                                    <span className="text-[11px] font-medium text-slate-700 text-center leading-tight line-clamp-2 mt-1.5 px-0.5">
                                                        {tab.title}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center text-slate-400">
                            <Search className="w-10 h-10 text-slate-300 mb-2" />
                            <p className="text-xs font-medium text-slate-600">
                                Không tìm thấy chức năng nào khớp với &quot;{searchKeyword}&quot;
                            </p>
                            <p className="text-[11px] text-slate-400 mt-1">
                                Vui lòng kiểm tra lại từ khóa hoặc quyền hạn của tài khoản
                            </p>
                        </div>
                    )}
                </ScrollArea>
            </SheetContent>
        </Sheet>
    );
}
